import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ApparelDetailModal = ({ apparel, onClose, onConsult }) => {
  if (!apparel) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-honda-glow">
        
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
            Detail Produk
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">

            {/* LEFT - IMAGE */}
            <div className="space-y-4">
              <div className="relative h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden bg-muted">
                <Image
                  src={apparel?.image}
                  alt={apparel?.imageAlt}
                  className="w-full h-full object-cover"
                />
                {apparel?.isGenuine && (
                  <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-4 py-2 rounded-full flex items-center space-x-2 shadow-honda-subtle">
                    <Icon name="Shield" size={18} />
                    <span className="text-sm font-heading font-bold">Genuine Honda</span>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT - DETAIL */}
            <div className="space-y-6">
              <div>
                <span className="text-sm text-muted-foreground font-medium">
                  {apparel?.category}
                </span>
                <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground mt-2">
                  {apparel?.name}
                </h3>
              </div>

              {/* PRICE & STOCK */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Harga</p>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary">
                    {formatPrice(apparel?.price)}
                  </p>
                </div>
                {apparel?.stock !== undefined && (
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground mb-1">Ketersediaan</p>
                    <p className={`text-lg font-heading font-bold ${
                      apparel?.stock > 0 ? 'text-success' : 'text-error'
                    }`}>
                      {apparel?.stock > 0 ? 'Tersedia' : 'Stok habis'}
                    </p>
                  </div>
                )}
              </div>

              {/* SIZE (KHUSUS APPAREL) */}
              {apparel?.sizes && apparel?.sizes.length > 0 && (
                <div>
                  <h4 className="text-lg font-heading font-bold text-foreground mb-2">
                    Ukuran
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {apparel.sizes.map((size, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 border rounded-lg text-sm font-heading font-semibold text-foreground bg-background"
                      >
                        {size}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* DESCRIPTION */}
              {apparel?.description && (
                <div>
                  <h4 className="text-lg font-heading font-bold text-foreground mb-2">
                    Deskripsi
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {apparel.description}
                  </p>
                </div>
              )}

              {/* CODE & WARRANTY */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Package" size={18} color="var(--color-primary)" />
                    <span className="text-xs text-muted-foreground">Kode Item</span>
                  </div>
                  <p className="text-sm font-heading font-bold text-foreground">
                    {apparel?.apparelCode || '—'}
                  </p>
                </div>

                {apparel?.warranty && (
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Award" size={18} color="var(--color-primary)" />
                      <span className="text-xs text-muted-foreground">Garansi</span>
                    </div>
                    <p className="text-sm font-heading font-bold text-foreground">
                      {apparel.warranty}
                    </p>
                  </div>
                )}
              </div>

              {/* SPECIFICATIONS */}
              {apparel?.specifications && apparel.specifications.length > 0 && (
                <div>
                  <h4 className="text-lg font-heading font-bold text-foreground mb-3">
                    Spesifikasi
                  </h4>
                  <div className="space-y-2">
                    {apparel.specifications.map((spec, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <span className="text-sm text-muted-foreground">
                          {spec.label}
                        </span>
                        <span className="text-sm font-heading font-semibold text-foreground">
                          {spec.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ACTION */}
              <div className="space-y-3 pt-4 border-t border-border">
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={() => onConsult(apparel)}
                >
                  Cek Ketersediaan
                </Button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApparelDetailModal;
